/**
 * Stable Circuit Breaker utility with exponential backoff and request deduplication
 * Designed to prevent infinite API loops and provide robust error handling
 */

class CircuitBreakerStable {
  constructor(name, options = {}) {
    this.name = name;

    // Configuration options
    this.failureThreshold = options.failureThreshold || 5;
    this.resetTimeout = options.resetTimeout || 60000; // 1 minute
    this.exponentialBase = options.exponentialBase || 2;
    this.maxBackoffTime = options.maxBackoffTime || 300000; // 5 minutes
    this.halfOpenMaxCalls = options.halfOpenMaxCalls || 3;

    // State management
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.successCount = 0;
    this.lastFailureTime = null;
    this.lastSuccessTime = null;
    this.nextAttemptTime = null;
    this.halfOpenCalls = 0;

    // Request deduplication
    this.pendingRequests = new Map();
    this.requestCache = new Map();
    this.cacheTimeout = options.cacheTimeout || 5000; // 5 seconds

    // Logging and monitoring
    this.metrics = {
      totalCalls: 0,
      successfulCalls: 0,
      failedCalls: 0,
      circuitOpenEvents: 0,
      lastStateChange: Date.now(),
    };

    this.logger = options.logger || console;
    this.enableDetailedLogging = options.enableDetailedLogging || false;
  }

  /**
   * Execute an operation with circuit breaker protection
   * @param {Function} operation - The operation to execute
   * @param {string} requestKey - Unique key for request deduplication
   * @returns {Promise} - The result of the operation
   */
  async execute(operation, requestKey = null) {
    this.metrics.totalCalls++;

    // Request deduplication
    if (requestKey && this.pendingRequests.has(requestKey)) {
      this.log('debug', `Deduplicating request: ${requestKey}`);
      return this.pendingRequests.get(requestKey);
    }

    // Check cache for recent successful results
    if (requestKey && this.requestCache.has(requestKey)) {
      const cached = this.requestCache.get(requestKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        this.log('debug', `Returning cached result for: ${requestKey}`);
        return cached.result;
      } else {
        this.requestCache.delete(requestKey);
      }
    }

    // Circuit breaker state check
    if (this.state === 'OPEN') {
      if (Date.now() < this.nextAttemptTime) {
        const waitTime = Math.round((this.nextAttemptTime - Date.now()) / 1000);
        this.log('warn', `Circuit is OPEN, rejecting call. Wait ${waitTime}s`);
        throw new CircuitBreakerError(
          `Circuit breaker is OPEN for ${this.name}. Retry in ${waitTime} seconds.`
        );
      } else {
        this.transitionToHalfOpen();
      }
    }

    if (this.state === 'HALF_OPEN' && this.halfOpenCalls >= this.halfOpenMaxCalls) {
      this.log('warn', `Half-open call limit reached, rejecting call`);
      throw new CircuitBreakerError(
        `Circuit breaker is in HALF_OPEN state with maximum calls reached for ${this.name}`
      );
    }

    // Execute the operation
    const promise = this.executeOperation(operation, requestKey);

    // Store pending request for deduplication
    if (requestKey) {
      this.pendingRequests.set(requestKey, promise);

      // Clean up pending request when done
      promise.finally(() => {
        this.pendingRequests.delete(requestKey);
      });
    }

    return promise;
  }

  /**
   * Internal method to execute the operation with proper error handling
   */
  async executeOperation(operation, requestKey) {
    if (this.state === 'HALF_OPEN') {
      this.halfOpenCalls++;
    }

    try {
      const startTime = Date.now();
      const result = await operation();
      const duration = Date.now() - startTime;

      this.onSuccess(duration);

      // Cache successful result
      if (requestKey) {
        this.requestCache.set(requestKey, {
          result: result,
          timestamp: Date.now(),
        });
      }

      return result;
    } catch (error) {
      this.onFailure(error);
      throw error;
    }
  }

  /**
   * Handle successful operation
   */
  onSuccess(duration = 0) {
    this.metrics.successfulCalls++;
    this.successCount++;
    this.lastSuccessTime = Date.now();

    if (this.state === 'HALF_OPEN') {
      if (this.successCount >= this.halfOpenMaxCalls) {
        this.transitionToClosed();
      }
    } else if (this.state === 'CLOSED') {
      // Reset failure count on success in closed state
      this.failureCount = 0;
    }

    this.log('debug', `Operation succeeded in ${duration}ms`);
  }

  /**
   * Handle failed operation with exponential backoff
   */
  onFailure(error) {
    this.metrics.failedCalls++;
    this.failureCount++;
    this.lastFailureTime = Date.now();

    this.log('warn', `Operation failed: ${error.message}`);

    if (this.state === 'HALF_OPEN') {
      this.transitionToOpen();
    } else if (this.failureCount >= this.failureThreshold) {
      this.transitionToOpen();
    }
  }

  /**
   * Transition to OPEN state with exponential backoff
   */
  transitionToOpen() {
    const previousState = this.state;
    this.state = 'OPEN';
    this.metrics.circuitOpenEvents++;
    this.metrics.lastStateChange = Date.now();

    // Calculate exponential backoff time
    const backoffMultiplier = Math.pow(
      this.exponentialBase,
      Math.min(this.failureCount - this.failureThreshold, 10)
    );
    const backoffTime = Math.min(this.resetTimeout * backoffMultiplier, this.maxBackoffTime);

    this.nextAttemptTime = Date.now() + backoffTime;
    this.halfOpenCalls = 0;

    this.log(
      'error',
      `Circuit transitioned from ${previousState} to OPEN. Backoff: ${Math.round(backoffTime / 1000)}s`
    );
  }

  /**
   * Transition to HALF_OPEN state
   */
  transitionToHalfOpen() {
    this.state = 'HALF_OPEN';
    this.metrics.lastStateChange = Date.now();
    this.halfOpenCalls = 0;
    this.successCount = 0;

    this.log('info', `Circuit transitioned to HALF_OPEN`);
  }

  /**
   * Transition to CLOSED state
   */
  transitionToClosed() {
    this.state = 'CLOSED';
    this.metrics.lastStateChange = Date.now();
    this.failureCount = 0;
    this.successCount = 0;
    this.halfOpenCalls = 0;
    this.nextAttemptTime = null;

    this.log('info', `Circuit transitioned to CLOSED`);
  }

  /**
   * Get current circuit breaker state and metrics
   */
  getState() {
    return {
      name: this.name,
      state: this.state,
      failureCount: this.failureCount,
      successCount: this.successCount,
      lastFailureTime: this.lastFailureTime,
      lastSuccessTime: this.lastSuccessTime,
      nextAttemptTime: this.nextAttemptTime,
      halfOpenCalls: this.halfOpenCalls,
      pendingRequests: this.pendingRequests.size,
      cachedResults: this.requestCache.size,
      metrics: { ...this.metrics },
    };
  }

  /**
   * Get health status
   */
  getHealthStatus() {
    const now = Date.now();
    const timeSinceLastFailure = this.lastFailureTime ? now - this.lastFailureTime : null;
    const timeSinceLastSuccess = this.lastSuccessTime ? now - this.lastSuccessTime : null;

    return {
      isHealthy: this.state === 'CLOSED' && this.failureCount === 0,
      state: this.state,
      failureRate:
        this.metrics.totalCalls > 0 ? this.metrics.failedCalls / this.metrics.totalCalls : 0,
      timeSinceLastFailure,
      timeSinceLastSuccess,
      nextAttemptIn: this.nextAttemptTime ? Math.max(0, this.nextAttemptTime - now) : null,
    };
  }

  /**
   * Reset circuit breaker to initial state
   */
  reset() {
    const previousState = this.state;
    this.state = 'CLOSED';
    this.failureCount = 0;
    this.successCount = 0;
    this.halfOpenCalls = 0;
    this.lastFailureTime = null;
    this.nextAttemptTime = null;
    this.metrics.lastStateChange = Date.now();

    // Clear caches
    this.pendingRequests.clear();
    this.requestCache.clear();

    this.log('info', `Circuit breaker reset from ${previousState} to CLOSED`);
  }

  /**
   * Clear request cache
   */
  clearCache() {
    const cacheSize = this.requestCache.size;
    this.requestCache.clear();
    this.log('debug', `Cleared ${cacheSize} cached results`);
  }

  /**
   * Cancel all pending requests
   */
  cancelPendingRequests() {
    const pendingCount = this.pendingRequests.size;
    this.pendingRequests.clear();
    this.log('debug', `Cancelled ${pendingCount} pending requests`);
  }

  /**
   * Internal logging method
   */
  log(level, message) {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] CircuitBreaker[${this.name}]: ${message}`;

    if (this.enableDetailedLogging || level === 'error' || level === 'warn') {
      if (this.logger[level]) {
        this.logger[level](logMessage);
      } else {
        this.logger.log(logMessage);
      }
    }
  }
}

/**
 * Custom error class for circuit breaker errors
 */
class CircuitBreakerError extends Error {
  constructor(message) {
    super(message);
    this.name = 'CircuitBreakerError';
    this.isCircuitBreakerError = true;
  }
}

/**
 * Circuit Breaker Manager for managing multiple circuit breakers
 */
class CircuitBreakerManager {
  constructor() {
    this.breakers = new Map();
  }

  /**
   * Get or create a circuit breaker
   */
  getBreaker(name, options = {}) {
    if (!this.breakers.has(name)) {
      this.breakers.set(name, new CircuitBreakerStable(name, options));
    }
    return this.breakers.get(name);
  }

  /**
   * Get all circuit breakers status
   */
  getAllStatus() {
    const status = {};
    for (const [name, breaker] of this.breakers) {
      status[name] = breaker.getState();
    }
    return status;
  }

  /**
   * Get health status of all circuit breakers
   */
  getHealthStatus() {
    const health = {};
    for (const [name, breaker] of this.breakers) {
      health[name] = breaker.getHealthStatus();
    }
    return health;
  }

  /**
   * Reset all circuit breakers
   */
  resetAll() {
    for (const breaker of this.breakers.values()) {
      breaker.reset();
    }
  }

  /**
   * Clear all caches
   */
  clearAllCaches() {
    for (const breaker of this.breakers.values()) {
      breaker.clearCache();
    }
  }
}

// Global circuit breaker manager instance
const circuitBreakerManager = new CircuitBreakerManager();

// Pre-configured circuit breakers for Event System
const eventSystemBreakers = {
  devices: circuitBreakerManager.getBreaker('event-system-devices', {
    failureThreshold: 3,
    resetTimeout: 30000,
    enableDetailedLogging: true,
  }),
  actions: circuitBreakerManager.getBreaker('event-system-actions', {
    failureThreshold: 3,
    resetTimeout: 30000,
    enableDetailedLogging: true,
  }),
  flows: circuitBreakerManager.getBreaker('event-system-flows', {
    failureThreshold: 3,
    resetTimeout: 30000,
    enableDetailedLogging: true,
  }),
  tenants: circuitBreakerManager.getBreaker('event-system-tenants', {
    failureThreshold: 2,
    resetTimeout: 15000,
    enableDetailedLogging: true,
  }),
};

export {
  CircuitBreakerStable,
  CircuitBreakerError,
  CircuitBreakerManager,
  circuitBreakerManager,
  eventSystemBreakers,
};

export default CircuitBreakerStable;
